import { T, RADIUS, SHADOW } from '@/lib/tokens';

export type CoachMarkStep = {
  title: string;
  body: string;
};

interface CoachMarkProps {
  step: CoachMarkStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onDismiss: () => void;
  /** Alignment hint — bottom shows tooltip at top of screen (for bottom-anchored targets) */
  position?: 'top' | 'bottom';
}

/**
 * Simple full-width coach mark banner that guides new invited users through key actions.
 * Renders as a fixed bottom/top panel rather than a positioned tooltip for reliability
 * across all screen sizes and scroll positions.
 */
export function CoachMark({ step, stepIndex, totalSteps, onNext, onDismiss, position = 'bottom' }: CoachMarkProps) {
  const isLast = stepIndex === totalSteps - 1;

  return (
    <>
      {/* Dimmed backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 40,
          background: 'rgba(0,0,0,0.3)',
          pointerEvents: 'none',
        }}
      />

      {/* Coach mark panel */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          ...(position === 'bottom' ? { bottom: 80 } : { top: 80 }),
          zIndex: 41,
          padding: '0 16px',
        }}
      >
        <div
          style={{
            background: T.accent,
            borderRadius: RADIUS.xl,
            boxShadow: SHADOW.lg,
            padding: '18px 20px',
            color: T.white,
          }}
        >
          {/* Step counter */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.8 }}>
              Tip {stepIndex + 1} of {totalSteps}
            </span>
            <button
              onClick={onDismiss}
              aria-label="Dismiss tips"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: T.white,
                opacity: 0.7,
                fontSize: 18,
                padding: 0,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          {/* Content */}
          <p style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px', color: T.white }}>
            {step.title}
          </p>
          <p style={{ fontSize: 13, margin: '0 0 14px', color: T.white, opacity: 0.9, lineHeight: 1.5 }}>
            {step.body}
          </p>

          {/* Action */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={isLast ? onDismiss : onNext}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: RADIUS.md,
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: T.white,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {isLast ? 'Got it' : 'Next tip →'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
